import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  type ChannelHealthDto,
  getChannelHealth,
  getSendConfig,
  getSystemHealth,
  type SystemHealthResponse,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import {
  channelLabel,
  type DrawerSection,
  deriveSendPosture,
  fmtUsd,
} from '../../lib/health-status.js';
import { useRealtimeConnected } from '../../lib/realtime.js';
import { Dialog } from '../ui/Dialog.js';

/**
 * R9 — the health drawer (a right slide-over via the Dialog primitive). The full
 * detail behind the topbar status pill + the alert dock: connection (SSE),
 * channels, worker, analysis failures, cost, backups. Opens to a specific
 * section when launched from an alert's action.
 */
export function HealthDrawer({
  open,
  section,
  onClose,
}: {
  open: boolean;
  section?: DrawerSection;
  onClose: () => void;
}) {
  const channelQ = useQuery({
    queryKey: ['channel-health'],
    queryFn: ({ signal }) => getChannelHealth({ signal }),
    enabled: open,
    staleTime: 60_000,
  });
  const systemQ = useQuery({
    queryKey: ['system-health'],
    queryFn: ({ signal }) => getSystemHealth({ signal }),
    enabled: open,
    staleTime: 30_000,
  });
  const sendQ = useQuery({
    queryKey: ['send-config'],
    queryFn: ({ signal }) => getSendConfig({ signal }),
    enabled: open,
    staleTime: 60_000,
  });
  const sseConnected = useRealtimeConnected();

  const sectionRefs = useRef<Partial<Record<DrawerSection | 'connection', HTMLElement | null>>>({});

  useEffect(() => {
    if (!open || !section) return;
    // Let the panel mount, then bring the requested section into view.
    const id = window.setTimeout(() => {
      sectionRefs.current[section]?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 60);
    return () => window.clearTimeout(id);
  }, [open, section]);

  const system = systemQ.data;
  const channels = channelQ.data?.channels ?? [];
  const posture = deriveSendPosture(sendQ.data);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="slideover"
      labelledBy="health-drawer-title"
      testId="health-drawer"
      className="health-drawer"
    >
      <header className="health-drawer-head">
        <h2 id="health-drawer-title">System health</h2>
        <button type="button" className="health-drawer-close" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </header>

      <div className="health-drawer-body">
        <Section
          title="Connection"
          refCb={(el) => {
            sectionRefs.current.connection = el;
          }}
        >
          <Row
            label="Real-time push"
            value={sseConnected ? 'Live · connected' : 'Reconnecting…'}
            tone={sseConnected ? 'ok' : 'warn'}
          />
          {posture ? (
            <Row label="Sending" value={posture.label} tone={posture.armed ? 'ok' : 'muted'} />
          ) : null}
        </Section>

        <Section
          title="Channels"
          refCb={(el) => {
            sectionRefs.current.channels = el;
          }}
        >
          {channelQ.data?.unipile_reachable === false ? (
            <p className="health-note" data-testid="unipile-unreachable">
              ⚠ The dashboard can't reach Unipile right now (the API call is failing) — channel
              health below is from the last good fetch, not verified now. See the "Unipile API key"
              alert for the fix.
            </p>
          ) : null}
          {channels.length === 0 ? (
            <p className="health-empty">
              {channelQ.isError ? 'Could not load channels.' : 'No channels.'}
            </p>
          ) : (
            channels.map((c) => <ChannelRow key={c.channel} channel={c} />)
          )}
        </Section>

        <Section
          title="Worker"
          refCb={(el) => {
            sectionRefs.current.worker = el;
          }}
        >
          <WorkerRows system={system} errored={systemQ.isError} />
        </Section>

        <Section
          title="Analysis"
          refCb={(el) => {
            sectionRefs.current.analyses = el;
          }}
        >
          {system ? (
            <>
              <Row
                label="Last 24h"
                value={`${system.analyses.success_24h} ok · ${system.analyses.failed_24h} failed`}
                tone={system.analyses.failure_alert ? 'warn' : 'ok'}
              />
              {system.analyses.recent_error_codes.length > 0 ? (
                <Row
                  label="Recent errors"
                  value={system.analyses.recent_error_codes.join(', ')}
                  tone="muted"
                />
              ) : null}
              {system.comment_drafts ? (
                <Row
                  label="LinkedIn AI drafts"
                  value={`${system.comment_drafts.failed_24h}/${system.comment_drafts.total_24h} failed (24h)`}
                  tone={system.comment_drafts.failure_alert ? 'warn' : 'muted'}
                />
              ) : null}
            </>
          ) : (
            <p className="health-empty">
              {systemQ.isError ? 'Could not load analysis stats.' : 'Loading…'}
            </p>
          )}
        </Section>

        <Section
          title="Cost"
          refCb={(el) => {
            sectionRefs.current.cost = el;
          }}
        >
          {system ? (
            <>
              <Row
                label="Today"
                value={fmtUsd(system.cost.today_usd)}
                tone={system.cost.spike ? 'warn' : 'ok'}
              />
              <Row label="Yesterday" value={fmtUsd(system.cost.yesterday_usd)} tone="muted" />
              <Row label="14-day avg" value={fmtUsd(system.cost.trailing_avg_usd)} tone="muted" />
            </>
          ) : (
            <p className="health-empty">{systemQ.isError ? 'Could not load cost.' : 'Loading…'}</p>
          )}
        </Section>

        <Section
          title="Backups"
          refCb={(el) => {
            sectionRefs.current.backups = el;
          }}
        >
          {system?.backups ? (
            <Row
              label="Off-host copies"
              value={
                system.backups.offhost_configured ? 'Configured' : 'Not configured (local-only)'
              }
              tone={system.backups.offhost_configured ? 'ok' : 'warn'}
            />
          ) : (
            <p className="health-empty">Backup status unavailable.</p>
          )}
        </Section>
      </div>
    </Dialog>
  );
}

function Section({
  title,
  refCb,
  children,
}: {
  title: string;
  refCb: (el: HTMLElement | null) => void;
  children: React.ReactNode;
}) {
  return (
    <section ref={refCb} className="health-section">
      <h3>{title}</h3>
      <div className="health-section-rows">{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'ok' | 'warn' | 'muted';
}) {
  return (
    <div className="health-row">
      <span className="health-row-label">{label}</span>
      <span className={`health-row-value tone-${tone}`}>{value}</span>
    </div>
  );
}

function ChannelRow({ channel: c }: { channel: ChannelHealthDto }) {
  const bad = c.connected && !c.healthy;
  const stale =
    c.connected && c.healthy && (c.webhook_subscribed === false || c.realtime_stale === true);
  const value = bad
    ? 'Disconnected'
    : stale
      ? 'No real-time'
      : c.connected
        ? 'Healthy'
        : 'Not connected';
  const tone = bad ? 'warn' : stale ? 'warn' : c.connected ? 'ok' : 'muted';
  const last = c.last_inbound_at
    ? `last in ${formatRelative(c.last_inbound_at)}`
    : 'no inbound yet';
  return (
    <div className="health-row">
      <span className="health-row-label">{channelLabel(c.channel)}</span>
      <span className={`health-row-value tone-${tone}`}>
        {value} · <span className="health-row-sub">{last}</span>
      </span>
    </div>
  );
}

function WorkerRows({
  system,
  errored,
}: {
  system: SystemHealthResponse | undefined;
  errored: boolean;
}) {
  if (!system)
    return <p className="health-empty">{errored ? 'Could not load worker status.' : 'Loading…'}</p>;
  const { worker } = system;
  return (
    <>
      <Row
        label="Status"
        value={worker.alive ? 'Alive' : 'Down'}
        tone={worker.alive ? 'ok' : 'warn'}
      />
      <Row
        label="Last sync"
        value={worker.last_sync_at ? formatRelative(worker.last_sync_at) : '—'}
        tone="muted"
      />
      <Row
        label="Last analysis"
        value={worker.last_analysis_at ? formatRelative(worker.last_analysis_at) : '—'}
        tone="muted"
      />
    </>
  );
}
